import { useCallback, useEffect, useState } from 'react';

import { useConfigValue } from 'hooks/configs/use-config-value';
import { setOptOut } from 'state/amplitude/amplitude-package';
import useBranch from 'state/branch/hooks/use-branch';
import { useBrazeContext } from 'state/braze';
import Braze from 'utils/braze';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import useOneTrustSdk from './use-one-trust-sdk';

type ConsentGroups = {
  C0001: number;
  C0002: number;
  C0003: number;
  C0004: number;
};

enum OneTrustGlobalConsentState {
  OPT_OUT = 'Opt-Out',
  OPT_IN = 'Opt-In',
}

export const OneTrustCookieBanner = () => {
  const { initBraze } = useBrazeContext();
  const { initBranch } = useBranch();
  const { loaded: otSdkLoaded } = useOneTrustSdk();
  const cookieCompliance = useConfigValue({ key: 'oneTrust', defaultValue: {} }).cookieCompliance;

  const [servicesLoaded, setServiceLoaded] = useState(false);
  const [otConsentGroups, setConsentGroups] = useState({} as ConsentGroups);
  const [consentGroupsSetInLocalStorage, setConsentGroupsSetInLocalStorage] = useState(false);
  // Function to initialize 3rd party services
  const initServicesWithCookies = useCallback(() => {
    if (!window.rbiBraze) {
      initBraze();
    }
    if (!window.branch) {
      initBranch();
    }
  }, [initBranch, initBraze]);

  // loads 3rd party services once OneTrust sdk is loaded
  useEffect(() => {
    if (otConsentGroups && otSdkLoaded && !servicesLoaded) {
      initServicesWithCookies();
    }
  }, [initServicesWithCookies, otConsentGroups, otSdkLoaded, servicesLoaded]);

  const servicesInitCheck = useCallback(() => {
    const areServicesInitialized = !!window.branch && !!window.rbiBraze;
    if (areServicesInitialized) {
      setServiceLoaded(true);
    }
    return areServicesInitialized;
  }, []);

  // Sets a interval to check if all 3rd party services are initialized
  useEffect(() => {
    const id = setInterval(servicesInitCheck, 1000);
    if (servicesLoaded) {
      clearInterval(id);
    }
    return () => clearInterval(id);
  }, [servicesInitCheck, servicesLoaded]);

  const getConsentGroups = (consentState: String[]) => {
    const groups: ConsentGroups = {
      C0001: 0,
      C0002: 0,
      C0003: 0,
      C0004: 0,
    };

    return Object.keys(groups).reduce((accumulator, group) => {
      if (consentState.includes(group)) {
        accumulator[group] = 1;
      }
      return accumulator;
    }, groups);
  };

  // Registers onetrust consent change handler
  useEffect(() => {
    const id = setInterval(() => {
      if (window?.OneTrust && otSdkLoaded) {
        window.OneTrust.OnConsentChanged((event: any) => {
          const consentState = event?.detail ?? [];
          const consentGroups = getConsentGroups(consentState);
          setConsentGroups(consentGroups);
          LocalStorage.setItem(StorageKeys.ONETRUST_COOKIE_CONSENT_GROUPS, consentGroups);
        });
        setConsentGroupsSetInLocalStorage(true);
      }
    }, 1000);
    if (consentGroupsSetInLocalStorage) {
      clearInterval(id);
    }
    return () => clearInterval(id);
  }, [otSdkLoaded, consentGroupsSetInLocalStorage]);

  // Pull the consent groups out of local storage if it's already populated.
  // This will trigger the hook below, and disable/enable any global services based on
  // the previously chosen settings.
  useEffect(() => {
    const existingConsentGroups = LocalStorage.getItem(StorageKeys.ONETRUST_COOKIE_CONSENT_GROUPS);
    if (existingConsentGroups) {
      setConsentGroups(existingConsentGroups);
    }
  }, []);

  // Switches off tracking for each service if data tracking is opted out
  useEffect(() => {
    const {
      globalConsentState,
      consentCategoryMParticle,
      consentCategoryBraze,
      consentCategoryBranch,
    } = cookieCompliance;

    // reads consent state for each consent category
    const mparticleConsentState = otConsentGroups[consentCategoryMParticle];
    const brazeConsentState = otConsentGroups[consentCategoryBraze];
    const branchConsentState = otConsentGroups[consentCategoryBranch];

    if (servicesLoaded) {
      // OneTrust is very confusing. Opt-out state means the user is not blocking us from tracking/selling them so this is a double negation
      // More below....
      const isDoNotSellOptOutState = globalConsentState === OneTrustGlobalConsentState.OPT_OUT;

      // Making this variable to make it human readable since the double negation is clearly mind bending
      const isOptedOutOfTracking = !isDoNotSellOptOutState;

      // Opt-out or Opt-in of MParticle if the user completely opted out of tracking or if it blocked Braze specifically
      const optOutOfMParticle = mparticleConsentState === 0 || isOptedOutOfTracking;
      setOptOut(optOutOfMParticle);

      // Stop or resume Braze if the user completely opted out of tracking or if it blocked Braze specifically
      if (brazeConsentState === 0 || isOptedOutOfTracking) {
        Braze.disableSDK();
      } else {
        Braze.enableSDK();
      }

      // Opt-out or Opt-in of Branch if the user completely opted out of tracking or if it blocked Branch specifically
      const optOutOfBranch = branchConsentState === 0 || isOptedOutOfTracking;
      window.branch?.disableTracking(optOutOfBranch);
    }
  }, [cookieCompliance, otConsentGroups, servicesLoaded]);

  return null;
};
